import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { ModalService, SiteService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { AccountType, FinancialAccount, SubledgerType } from '../../../models/financial-account/financial-account';
import { FinancialAccountProxyService } from '../../../proxies/financial-account.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { MaskedAmountPipe } from '../../../pipes/masked-amount.pipe';
import { Currency, currencyLabel } from '../../../utilities/currency.util';
import { AccountCreateModalComponent } from '../_modals/account-create-modal/account-create-modal.component';
import { AccountEditModalComponent } from '../_modals/account-edit-modal/account-edit-modal.component';

interface AccountRow {
  id: string;
  accountNumber: string;
  name: string;
  accountType: AccountType;
  typeLabel: string;
  /** `null` when the API redacted this balance, or when a descendant's redaction propagated up. */
  balance: number | null;
  isGroup: boolean;
  isReconciliation: boolean;
  subledgerType?: SubledgerType | null;
}

interface TypeChip {
  label: string;
  value: AccountType | null;
}

/**
 * Level 1 — Chart of Accounts. Renders the company's account hierarchy as a PrimeNG tree table with
 * rolled-up balances, search + type filtering, and CSV export. Reconciliation (control) accounts are
 * marked and link into their subledger (Level 2).
 */
@Component({
  selector: 'app-chart-of-accounts',
  templateUrl: './chart-of-accounts.component.html',
  styleUrl: './chart-of-accounts.component.scss',
})
export class ChartOfAccountsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly proxy = inject(FinancialAccountProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);

  readonly isLoading = signal(0);
  readonly accounts = signal<FinancialAccount[]>([]);
  private readonly companyId = signal<string | null>(null);
  /** The owning company's currency — seeds new accounts so they default to the right currency. */
  private readonly companyCurrency = signal<Currency>(Currency.HNL);
  /** The company's functional currency — the currency the rolled-up GL balances are kept in (#4549). */
  private readonly functionalCurrency = signal<Currency>(Currency.USD);
  readonly searchTerm = signal('');
  readonly typeFilter = signal<AccountType | null>(null);

  readonly typeChips: TypeChip[] = [
    { label: this.t('COA_All', 'All'), value: null },
    { label: this.t('COA_Assets', 'Assets'), value: AccountType.Asset },
    { label: this.t('COA_Liabilities', 'Liabilities'), value: AccountType.Liability },
    { label: this.t('COA_Revenue', 'Revenue'), value: AccountType.Revenue },
    { label: this.t('COA_Expense', 'Expense'), value: AccountType.Expense },
  ];

  /** Currency shown in the balance column: the functional currency the rolled-up GL balances are kept in (#4549). */
  readonly currency = computed(() => currencyLabel(this.functionalCurrency()));

  /**
   * Rolled-up balance per account id (group = sum of descendant posting balances), or `null` where
   * the figure is withheld.
   *
   * Redaction propagates upward: one unknown descendant makes the parent's total unknown too. Summing
   * the known children instead would print a total that is quietly wrong, and — worse — subtracting it
   * from a sibling total would hand back the very balance the API refused to send.
   */
  private readonly rollups = computed(() => {
    const childrenByParent = this.childrenByParent(this.accounts());
    const cache = new Map<string, number | null>();
    const roll = (a: FinancialAccount): number | null => {
      if (a.id && cache.has(a.id)) return cache.get(a.id)!;
      const kids = childrenByParent.get(a.id ?? '') ?? [];
      let value: number | null;
      if (kids.length) {
        const rolled = kids.map(roll);
        value = rolled.some((v) => v === null) ? null : rolled.reduce((s: number, v) => s + (v as number), 0);
      } else {
        value = a.balance ?? null;
      }
      if (a.id) cache.set(a.id, value);
      return value;
    };
    this.accounts().forEach(roll);
    return cache;
  });

  /** Number of accounts matching the active filters (shown next to the search). */
  readonly matchCount = computed(() => this.matchingIds().size);

  readonly treeNodes = computed<TreeNode<AccountRow>[]>(() => {
    const all = this.accounts();
    const include = this.includedIds();
    const visible = all.filter((a) => a.id && include.has(a.id));
    return this.buildTree(visible);
  });

  ngOnInit(): void {
    this.load();
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private load(): void {
    this.startLoading();
    this.siteCompany.getFinancialCompany(SiteService.getSelectedSiteId()).subscribe({
      next: (company) => {
        if (!company?.id) {
          this.stopLoading();
          return;
        }
        this.companyId.set(company.id);
        this.companyCurrency.set(company.currency ?? Currency.HNL);
        this.functionalCurrency.set(company.functionalCurrency ?? Currency.USD);
        this.proxy
          .getByCompany(company.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (response) => this.accounts.set(response.data ?? []),
            error: () => this.accounts.set([]),
          });
      },
      error: () => this.stopLoading(),
    });
  }

  // ── Filtering ─────────────────────────────────────────────────────────────────
  private readonly matchingIds = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const type = this.typeFilter();
    const ids = new Set<string>();
    for (const a of this.accounts()) {
      if (!a.id) continue;
      if (type != null && a.accountType !== type) continue;
      if (term && !a.accountNumber.toLowerCase().includes(term) && !this.localized(a).toLowerCase().includes(term)) continue;
      ids.add(a.id);
    }
    return ids;
  });

  /** Matches plus all their ancestors, so the hierarchy stays navigable while filtering. */
  private readonly includedIds = computed(() => {
    const byId = new Map(
      this.accounts()
        .filter((a) => a.id)
        .map((a) => [a.id!, a]),
    );
    const include = new Set<string>();
    for (const id of this.matchingIds()) {
      let current: FinancialAccount | undefined = byId.get(id);
      while (current?.id && !include.has(current.id)) {
        include.add(current.id);
        current = current.parentAccountId ? byId.get(current.parentAccountId) : undefined;
      }
    }
    return include;
  });

  setType(type: AccountType | null): void {
    this.typeFilter.set(type);
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
  }

  // ── Tree building ─────────────────────────────────────────────────────────────
  private childrenByParent(accounts: FinancialAccount[]): Map<string, FinancialAccount[]> {
    const map = new Map<string, FinancialAccount[]>();
    for (const a of accounts) {
      const key = a.parentAccountId ?? '';
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    return map;
  }

  private buildTree(accounts: FinancialAccount[]): TreeNode<AccountRow>[] {
    const childrenByParent = this.childrenByParent(accounts);
    const byNumber = (a: FinancialAccount, b: FinancialAccount) => a.accountNumber.localeCompare(b.accountNumber);
    const make = (a: FinancialAccount): TreeNode<AccountRow> => {
      const kids = (childrenByParent.get(a.id ?? '') ?? []).slice().sort(byNumber);
      return {
        key: a.id,
        data: this.toRow(a, kids.length > 0),
        children: kids.map(make),
        expanded: kids.length > 0,
        leaf: kids.length === 0,
      };
    };
    return (childrenByParent.get('') ?? []).slice().sort(byNumber).map(make);
  }

  private toRow(a: FinancialAccount, isGroup: boolean): AccountRow {
    return {
      id: a.id!,
      accountNumber: a.accountNumber,
      name: this.localized(a),
      accountType: a.accountType,
      typeLabel: this.typeLabel(a.accountType),
      // ?? null, never ?? 0 — a redaction must stay distinguishable from an empty account.
      balance: a.id ? (this.rollups().get(a.id) ?? null) : null,
      isGroup,
      isReconciliation: a.subledgerType != null,
      subledgerType: a.subledgerType,
    };
  }

  /** CSV cell for a balance: the figure, or the same mask the table shows when it is withheld. */
  private exportBalance(a: FinancialAccount): string {
    const value = a.id ? (this.rollups().get(a.id) ?? null) : null;
    return value === null ? MaskedAmountPipe.MASK : value.toFixed(2);
  }

  private localized(a: FinancialAccount): string {
    return this.locale.getLocalizedValue(a.accountName) ?? a.accountNumber;
  }

  private typeLabel(type: AccountType): string {
    switch (type) {
      case AccountType.Asset:
        return this.t('COA_Asset', 'Asset');
      case AccountType.Liability:
        return this.t('COA_Liability', 'Liability');
      case AccountType.Equity:
        return this.t('COA_Equity', 'Equity');
      case AccountType.Revenue:
        return this.t('COA_RevenueType', 'Revenue');
      case AccountType.Expense:
        return this.t('COA_ExpenseType', 'Expense');
      default:
        return '';
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────────
  openSubledger(row: AccountRow): void {
    if (row.isReconciliation) this.router.navigate(['/chart-of-accounts/subledger'], { queryParams: { accountId: row.id } });
  }

  /** Open the create-account modal, optionally pre-set under a parent row (the "+" on a tree node). */
  openCreate(parentRow?: AccountRow | null): void {
    const company = this.companyId();
    if (!company) return;
    const parent = parentRow ? (this.accounts().find((a) => a.id === parentRow.id) ?? null) : null;
    this.modalService
      .open(
        AccountCreateModalComponent,
        { modalClass: 'modal_small' },
        { parent, accounts: this.accounts(), companyReference: company, defaultCurrency: this.companyCurrency() },
      )
      .subscribe((result) => {
        if (result?.action === 'save' && result.data) this.createAccount(result.data);
      });
  }

  /** Open the edit modal for a row. Only the name and authorized posting modules are editable there. */
  openEdit(row: AccountRow): void {
    const account = this.accounts().find((a) => a.id === row.id);
    if (!account) return;
    this.modalService
      .open(AccountEditModalComponent, { modalClass: 'modal_small' }, { account, accounts: this.accounts() })
      .subscribe((result) => {
        if (result?.action === 'save' && result.data) this.updateAccount(result.data);
      });
  }

  private updateAccount(account: FinancialAccount): void {
    if (!account.id) return;
    this.startLoading();
    this.proxy
      .update(account.id, account)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success) return;
          GlobalMessageUtil.success(this.t('COA_Title', 'Chart of Accounts'), this.t('COA_EditAccount_Saved', 'Account updated.'));
          this.proxy.clearCompanyCache(this.companyId() ?? undefined);
          this.load();
        },
      });
  }

  private createAccount(account: FinancialAccount): void {
    this.startLoading();
    this.proxy
      .create(account)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success) return;
          GlobalMessageUtil.success(this.t('COA_Title', 'Chart of Accounts'), this.t('COA_NewAccount_Created', 'Account created.'));
          this.proxy.clearCompanyCache(this.companyId() ?? undefined);
          this.load();
        },
      });
  }

  export(): void {
    const header = ['Account', 'Name', 'Type', `Balance (${this.currency()})`];
    const rows = this.accounts()
      .slice()
      .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
      .map((a) => [
        a.accountNumber,
        this.localized(a),
        this.typeLabel(a.accountType),
        // A redacted balance exports as the same mask it shows on screen. Exporting 0.00 would put a
        // number the user was never shown into a spreadsheet, where nothing marks it as withheld.
        this.exportBalance(a),
      ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'chart-of-accounts.csv';
    link.click();
    URL.revokeObjectURL(url);
  }
}
